import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

export default function EmailVerificationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      setErrors({ code: 'Verification code is required' });
      return;
    }

    setLoading(true);
    try {
      await API.post('/api/auth/verify-email', {
        user_id: user?.id,
        code: code,
      });

      setVerified(true);
      setTimeout(() => {
        navigate('/profile-setup');
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Verification failed. Please try again.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      setResendTimer(60);
      setErrors({});
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to resend code.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  if (verified) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>✓ Email Verified!</h1>
            <p>Your email has been verified successfully</p>
          </div>
          <div className="success-message">
            <p>Redirecting you to the app...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Verify Your Email</h1>
          <p>We've sent a verification code to your email</p>
        </div>

        {errors.submit && (
          <div className="error-banner">{errors.submit}</div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setErrors({ ...errors, code: '' });
              }}
              placeholder="Enter 6-digit code"
              className={errors.code ? 'input-error' : ''}
              disabled={loading}
              maxLength={6}
            />
            {errors.code && <span className="error-text">{errors.code}</span>}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Didn't receive the code? Check your spam folder</p>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading || resendTimer > 0}
            className="link-button"
          >
            {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
          </button>
        </div>
      </div>
    </div>
  );
}